import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { maskCNPJ, maskCPF, maskPhone, removeMask } from '@/utils/masks';
import { SERVICE_MODALITY_TRANSLATIONS } from '@/constants/professional/translations';
import { Button } from '@/components/ui/Button';
import {
	User,
	Phone,
	Calendar,
	FileText,
	CircleChevronLeft,
	Save,
	Mail,
	IdCard,
	Building,
	Camera,
	BriefcaseMedical,
	Loader2,
	ExternalLink,
} from 'lucide-react';

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Input,
	InputError,
	InputLabel,
	InputWrapper,
	CopyWrapper,
	ToggleInput,
} from '@/components';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { ProfessionalPublicTagsFields } from '@/components/professional/forms/ProfessionalPublicTagsFields';
import {
	APPROACHES,
	FOCUS_TOPICS,
	HIGHLIGHT_TAGS,
	MAX_FOCUS_TOPICS,
	MAX_SECONDARY_APPROACHES,
	isPsychologyServiceName,
	type ProfessionalFocusTopic,
	type ProfessionalHighlightTag,
	type PsychologyApproach,
	GENDERS,
	getGenderLabel,
} from '@/constants/professionalTags';

const HIGHLIGHT_TAG_KEYS = HIGHLIGHT_TAGS.map((t) => t.key) as [
	ProfessionalHighlightTag,
	...ProfessionalHighlightTag[],
];
const APPROACH_KEYS = APPROACHES.map((a) => a.key) as [
	PsychologyApproach,
	...PsychologyApproach[],
];
const FOCUS_TOPIC_KEYS = FOCUS_TOPICS.map((t) => t.key) as [
	ProfessionalFocusTopic,
	...ProfessionalFocusTopic[],
];

// Definição constante para sincronizar Zod e UI com o tipo Professionals.BrazilianState
const BRAZILIAN_STATES: [
	Professionals.BrazilianState,
	...Professionals.BrazilianState[],
] = [
	'AC',
	'AL',
	'AP',
	'AM',
	'BA',
	'CE',
	'DF',
	'ES',
	'GO',
	'MA',
	'MT',
	'MS',
	'MG',
	'PA',
	'PB',
	'PR',
	'PE',
	'PI',
	'RJ',
	'RN',
	'RS',
	'RO',
	'RR',
	'SC',
	'SP',
	'SE',
	'TO',
];

/** Radix Select não aceita value vazio; usamos sentinela e gravamos `null` no form. */
const CLINIC_SELECT_NONE = '__clinic_none__';

const emptyToFormString = (v: unknown) =>
	v == null || v === undefined ? '' : String(v);

/** Campo de texto opcional: vazio → `null` para o backend. */
const optionalTrimmedNull = (max?: number) =>
	z
		.preprocess(emptyToFormString, z.string())
		.transform((s) => s.trim())
		.pipe(
			max != null
				? z
						.string()
						.max(max, `Máximo ${max} caracteres`)
						.transform((s) => (s === '' ? null : s))
				: z.string().transform((s) => (s === '' ? null : s)),
		);

const optionalUrlToNull = z
	.preprocess(emptyToFormString, z.string())
	.transform((s) => s.trim())
	.refine(
		(val) => val.length === 0 || z.string().url().safeParse(val).success,
		{ message: 'URL inválida' },
	)
	.transform((val) => (val.length === 0 ? null : val));

export const professionalSchema = z.object({
	email: z.string().email(),
	clinic_id: z
		.string()
		.uuid('ID da clínica inválido')
		.or(z.literal(''))
		.or(z.null()),
	service_modality: z.enum(['HYBRID', 'FULL_REMOTE', 'FULL_PRESENCIAL']),
	first_name: z
		.string()
		.min(1, 'Nome é obrigatório')
		.min(3, 'Nome deve ter no mínimo 3 caracteres')
		.max(255, 'Nome deve ter no máximo 255 caracteres')
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Apenas letras e acentos são permitidos',
		),
	last_name: z
		.string()
		.min(1, 'Sobrenome é obrigatório')
		.min(3, 'Sobrenome deve ter no mínimo 3 caracteres')
		.max(255, 'Sobrenome deve ter no máximo 255 caracteres')
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Apenas letras e acentos são permitidos',
		),
	name_prefix: z
		.preprocess(emptyToFormString, z.string())
		.transform((s) => s.trim())
		.refine((s) => s.length <= 32, { message: 'Máximo 32 caracteres' })
		.transform((s) => (s === '' ? null : s)),
	gender: z
		.enum(['FEMININO', 'MASCULINO', 'PREFIRO_NAO_INFORMAR'])
		.optional(),
	phone_number: z
		.string()
		.transform(removeMask)
		.refine(
			(val) => val.length === 0 || val.length === 11,
			'Telefone deve conter 11 números',
		)
		.transform((val) => (val.length === 0 ? null : val)),
	birthday: z.string().min(1, 'Data de nascimento é obrigatória'),
	cpf_or_cnpj: z
		.string()
		.min(1, 'CPF/CNPJ é obrigatório')
		.transform(removeMask)
		.refine(
			(val) => val.length === 11 || val.length === 14,
			'Documento inválido',
		),
	crm_or_crp: z
		.string()
		.min(1, 'CRM/CRP é obrigatório')
		.regex(/^[A-Za-z0-9\/-]+$/, 'Formato inválido'),
	document_state: z.enum(BRAZILIAN_STATES), // Uso da constante tipada
	rqe: optionalTrimmedNull(),
	pos_degree: z.boolean().optional(),
	lattes_url: optionalUrlToNull,
	video_url: optionalUrlToNull,
	brief_description: optionalTrimmedNull(255),
	profile_photo_url: z.string().optional(),
	last_login: z.string().optional(),
	// Exibição pública (tags do card). Vazio → null / [] para o backend.
	highlight_tag: z
		.enum(HIGHLIGHT_TAG_KEYS)
		.nullable()
		.optional()
		.transform((v) => v ?? null),
	main_approach: z
		.enum(APPROACH_KEYS)
		.nullable()
		.optional()
		.transform((v) => v ?? null),
	secondary_approaches: z
		.array(z.enum(APPROACH_KEYS))
		.max(
			MAX_SECONDARY_APPROACHES,
			`Máximo ${MAX_SECONDARY_APPROACHES} abordagens complementares`,
		)
		.optional()
		.transform((v) => v ?? []),
	focus_topics: z
		.array(z.enum(FOCUS_TOPIC_KEYS))
		.max(MAX_FOCUS_TOPICS, `Máximo ${MAX_FOCUS_TOPICS} temas`)
		.optional()
		.transform((v) => v ?? []),
});

type ProfessionalFormInput = z.input<typeof professionalSchema>;
type ProfessionalFormValues = z.output<typeof professionalSchema>;

interface ProfessionalFormProps {
	onSubmit: (data: ProfessionalFormValues) => void;
	onBack?: () => void;
	defaultValues?: Partial<Professionals.Professional>;
	isSubmitting?: boolean;
	formTitle?: string;
	submitButtonText?: string;
	clinics: Array<Clinics.Clinic>;
}

const ProfessionalForm: React.FC<ProfessionalFormProps> = ({
	onSubmit,
	defaultValues,
	isSubmitting: externalIsSubmitting = false,
	formTitle,
	submitButtonText,
	onBack,
	clinics,
}) => {
	const {
		register,
		handleSubmit,
		formState: {
			errors,
			isSubmitting: internalIsSubmitting,
			isDirty,
			dirtyFields,
		},
		watch,
		setValue,
		control,
		reset,
	} = useForm<ProfessionalFormInput, unknown, ProfessionalFormValues>({
		resolver: zodResolver(professionalSchema),
		defaultValues: defaultValues as any,
	});

	useEffect(() => {
		if (defaultValues) {
			const valuesToSet = { ...defaultValues };
			if (defaultValues.birthday) {
				valuesToSet.birthday = new Date(defaultValues.birthday)
					.toISOString()
					.split('T')[0];
			}
			valuesToSet.phone_number =
				defaultValues.phone_number != null
					? String(defaultValues.phone_number)
					: '';
			valuesToSet.name_prefix = defaultValues.name_prefix ?? '';
			valuesToSet.gender = defaultValues.gender ?? 'PREFIRO_NAO_INFORMAR';
			valuesToSet.rqe = defaultValues.rqe ?? '';
			valuesToSet.lattes_url = defaultValues.lattes_url ?? '';
			valuesToSet.video_url = defaultValues.video_url ?? '';
			valuesToSet.brief_description =
				defaultValues.brief_description ?? '';
			valuesToSet.highlight_tag = defaultValues.highlight_tag ?? null;
			valuesToSet.main_approach = defaultValues.main_approach ?? null;
			valuesToSet.secondary_approaches = [
				...(defaultValues.secondary_approaches ?? []),
			];
			valuesToSet.focus_topics = [...(defaultValues.focus_topics ?? [])];
			reset(valuesToSet as any);
		}
	}, [defaultValues, reset]);

	const isSubmitting = externalIsSubmitting || internalIsSubmitting;

	const findClinicName = (id: string | null | undefined) => {
		if (id == null || id === '') return '';
		return clinics.find((clinic) => clinic.id === id)?.name || '';
	};

	const clinicId = watch('clinic_id');
	const clinicSelectValue =
		clinicId != null && clinicId !== '' ? clinicId : CLINIC_SELECT_NONE;

	const lattesUrlTrimmed = String(watch('lattes_url') ?? '').trim();
	const videoUrlTrimmed = String(watch('video_url') ?? '').trim();

	const publicTagsValue = {
		highlight_tag: watch('highlight_tag') ?? null,
		main_approach: watch('main_approach') ?? null,
		secondary_approaches: watch('secondary_approaches') ?? [],
		focus_topics: watch('focus_topics') ?? [],
	};
	const professionalService =
		defaultValues?.professional_services?.[0]?.service;
	const isPsychology = isPsychologyServiceName(
		professionalService?.service_name,
		professionalService?.professional_label,
	);

	const handlePhotoClick = () => {
		toast.info('Funcionalidade em breve');
	};

	return (
		<form
			onSubmit={handleSubmit((data) => {
				const dirtyKeys = Object.keys(dirtyFields) as Array<
					keyof ProfessionalFormInput
				>;

				const entries = dirtyKeys
					.filter(
						(key) => dirtyFields[key as keyof typeof dirtyFields],
					)
					.map(
						(key) => [key, data[key as keyof typeof data]] as const,
					);

				const patch = Object.fromEntries(
					entries,
				) as Partial<ProfessionalFormValues>;

				onSubmit(patch as ProfessionalFormValues);
			})}
			className="w-full space-y-8 bg-inherit"
		>
			{formTitle && (
				<div className="flex w-full flex-wrap items-center justify-between">
					<h2 className="text-lg font-bold text-zinc-900">
						{formTitle}
					</h2>
				</div>
			)}

			<h2 className="text-lg font-bold text-zinc-900">Editar dados</h2>

			<div className="space-y-6">
				<h3 className="text-base font-bold text-zinc-900">
					Informações Pessoais
				</h3>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Nome"
							icon={<User className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input {...register('first_name')} />
						</InputWrapper>
						{errors.first_name?.message && (
							<InputError error={errors.first_name.message} />
						)}
					</div>
					<div className="space-y-1">
						<InputLabel
							text="Sobrenome"
							icon={<User className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input {...register('last_name')} />
						</InputWrapper>
						{errors.last_name?.message && (
							<InputError error={errors.last_name.message} />
						)}
					</div>
				</div>

				<div className="space-y-1">
					<InputLabel
						text="Prefixo (opcional)"
						icon={<User className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							{...register('name_prefix')}
							placeholder="Dr., Dra., etc."
						/>
					</InputWrapper>
					{errors.name_prefix?.message && (
						<InputError error={errors.name_prefix.message} />
					)}
				</div>

				<div className="space-y-1">
					<InputLabel
						text="Gênero (opcional)"
						icon={<User className="h-4 w-4 text-dark" />}
					/>
					<Select
						value={watch('gender') ?? 'PREFIRO_NAO_INFORMAR'}
						onValueChange={(v) =>
							setValue('gender', v as any, { shouldDirty: true })
						}
					>
						<SelectTrigger className="h-fit w-full">
							<SelectValue>
								{getGenderLabel(watch('gender'))}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{GENDERS.map((g) => (
								<SelectItem key={g.key} value={g.key}>
									{g.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Email"
							icon={<Mail className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input readOnly {...register('email')} />
						</InputWrapper>
					</div>
					<div className="space-y-1">
						<InputLabel
							text="Foto de perfil"
							icon={<Camera className="h-4 w-4 text-zinc-400" />}
						/>
						<button
							type="button"
							onClick={handlePhotoClick}
							className="group relative flex h-[42px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 bg-zinc-50 py-2.5 pl-10 pr-4 transition-all hover:border-zinc-300 hover:bg-zinc-100"
						>
							<Camera
								size={16}
								className="absolute left-3 text-zinc-400 transition-colors group-hover:text-zinc-900"
							/>
							<span className="text-sm text-zinc-500">
								Escolha uma imagem
							</span>
						</button>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Data de Nascimento"
							icon={<Calendar className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input type="date" {...register('birthday')} />
						</InputWrapper>
						{errors.birthday?.message && (
							<InputError error={errors.birthday.message} />
						)}
					</div>
					<div className="space-y-1">
						<InputLabel
							text="Telefone (opcional)"
							icon={<Phone className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								value={maskPhone(
									String(watch('phone_number') ?? ''),
								)}
								{...register('phone_number')}
							/>
						</InputWrapper>
						{errors.phone_number?.message && (
							<InputError error={errors.phone_number.message} />
						)}
					</div>
				</div>
			</div>

			<div className="space-y-4">
				<h3 className="text-base font-bold text-zinc-900">
					Informações Profissionais
				</h3>
				<div className="space-y-1">
					<InputLabel
						text="Modalidade de atendimento"
						icon={
							<BriefcaseMedical className="h-4 w-4 text-dark" />
						}
					/>
					<Select
						value={watch('service_modality')}
						onValueChange={(v) =>
							setValue('service_modality', v as any, {
								shouldDirty: true,
							})
						}
					>
						<SelectTrigger className="h-fit w-full">
							<SelectValue>
								{watch('service_modality')
									? SERVICE_MODALITY_TRANSLATIONS[
											watch(
												'service_modality',
											) as Professionals.ServiceModality
									  ]
									: 'Escolher modalidade'}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{['FULL_PRESENCIAL', 'FULL_REMOTE', 'HYBRID'].map(
								(modality) => (
									<SelectItem key={modality} value={modality}>
										{
											SERVICE_MODALITY_TRANSLATIONS[
												modality as Professionals.ServiceModality
											]
										}
									</SelectItem>
								),
							)}
						</SelectContent>
					</Select>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Registro Profissional (CRM/CRP)"
							icon={<FileText className="h-4 w-4 text-dark" />}
						/>
						<CopyWrapper
							valueToCopy={String(watch('crm_or_crp') ?? '')}
						>
							<InputWrapper>
								<Input {...register('crm_or_crp')} />
							</InputWrapper>
						</CopyWrapper>
					</div>
					<div className="space-y-1">
						<InputLabel
							text="RQE (Opcional)"
							icon={<FileText className="h-4 w-4 text-dark" />}
						/>
						<CopyWrapper valueToCopy={String(watch('rqe') ?? '')}>
							<InputWrapper>
								<Input {...register('rqe')} />
							</InputWrapper>
						</CopyWrapper>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="CPF/CNPJ"
							icon={<IdCard className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								{...register('cpf_or_cnpj')}
								value={
									String(watch('cpf_or_cnpj') ?? '').length <=
									11
										? maskCPF(
												String(
													watch('cpf_or_cnpj') ?? '',
												),
										  )
										: maskCNPJ(
												String(
													watch('cpf_or_cnpj') ?? '',
												),
										  )
								}
							/>
						</InputWrapper>
					</div>
					<div className="space-y-1">
						<InputLabel
							text="Estado do Documento (UF)"
							icon={<FileText className="h-4 w-4 text-dark" />}
						/>
						<Select
							value={watch('document_state')}
							onValueChange={(v) =>
								setValue(
									'document_state',
									v as Professionals.BrazilianState,
									{
										shouldDirty: true,
									},
								)
							}
						>
							<SelectTrigger className="h-fit w-full">
								<SelectValue>
									{watch('document_state') || 'Selecionar'}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								{BRAZILIAN_STATES.map((s) => (
									<SelectItem key={s} value={s}>
										{s}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Clínica"
							icon={<Building className="h-4 w-4 text-dark" />}
						/>
						<Select
							value={clinicSelectValue}
							onValueChange={(v) =>
								setValue(
									'clinic_id',
									v === CLINIC_SELECT_NONE ? null : v,
									{
										shouldDirty: true,
									},
								)
							}
						>
							<SelectTrigger className="h-fit w-full">
								<SelectValue>
									{findClinicName(clinicId) || 'Sem clínica'}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={CLINIC_SELECT_NONE}>
									Sem clínica
								</SelectItem>
								{clinics.map((c) => (
									<SelectItem key={c.id} value={c.id}>
										{c.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<ToggleInput
						control={control}
						name="pos_degree"
						label="Possui Pós-Graduação?"
					/>
				</div>

				<div className="space-y-1">
					<InputLabel
						text="Descrição breve (opcional)"
						icon={<FileText className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper className="min-h-[80px] items-start">
						<textarea
							{...register('brief_description')}
							className="flex-1 resize-none border-0 bg-transparent p-0 text-sm text-dark outline-none placeholder:text-zinc-500 focus:outline-none"
							rows={3}
							maxLength={255}
							placeholder="Especialista em..."
						/>
					</InputWrapper>
					<p className="text-xs text-zinc-400">
						{String(watch('brief_description') ?? '').length}/255
					</p>
					{errors.brief_description?.message && (
						<InputError error={errors.brief_description.message} />
					)}
				</div>

				<div className="space-y-1">
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
						<InputLabel
							text="URL do Lattes (opcional)"
							icon={<FileText className="h-4 w-4 text-dark" />}
						/>
						{lattesUrlTrimmed ? (
							<a
								href={lattesUrlTrimmed}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 underline-offset-2 hover:underline"
							>
								Abrir Lattes
								<ExternalLink className="size-3.5 shrink-0" />
							</a>
						) : (
							<span className="text-sm text-zinc-400">
								Sem Lattes cadastrado
							</span>
						)}
					</div>
					<CopyWrapper
						valueToCopy={String(watch('lattes_url') ?? '')}
					>
						<InputWrapper>
							<Input {...register('lattes_url')} />
						</InputWrapper>
					</CopyWrapper>
				</div>

				<div className="space-y-1">
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
						<InputLabel
							text="URL do vídeo (opcional)"
							icon={<FileText className="h-4 w-4 text-dark" />}
						/>
						{videoUrlTrimmed ? (
							<a
								href={videoUrlTrimmed}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 underline-offset-2 hover:underline"
							>
								Abrir vídeo
								<ExternalLink className="size-3.5 shrink-0" />
							</a>
						) : (
							<span className="text-sm text-zinc-400">
								Sem vídeo cadastrado
							</span>
						)}
					</div>
					<CopyWrapper valueToCopy={String(watch('video_url') ?? '')}>
						<InputWrapper>
							<Input {...register('video_url')} />
						</InputWrapper>
					</CopyWrapper>
				</div>
			</div>

			<div className="space-y-4">
				<div>
					<h3 className="text-base font-bold text-zinc-900">
						Exibição pública (tags)
					</h3>
					<p className="text-xs text-zinc-400">
						Tag de destaque, abordagens e temas mostrados nos cards
						do profissional.
					</p>
				</div>
				<ProfessionalPublicTagsFields
					value={publicTagsValue}
					onChange={(next) => {
						setValue('highlight_tag', next.highlight_tag, {
							shouldDirty: true,
						});
						setValue('main_approach', next.main_approach, {
							shouldDirty: true,
						});
						setValue(
							'secondary_approaches',
							next.secondary_approaches,
							{ shouldDirty: true },
						);
						setValue('focus_topics', next.focus_topics, {
							shouldDirty: true,
						});
					}}
					requireMainApproach={isPsychology}
					errors={{
						highlight_tag: errors.highlight_tag?.message,
						main_approach: errors.main_approach?.message,
						secondary_approaches:
							errors.secondary_approaches?.message,
						focus_topics: errors.focus_topics?.message,
					}}
					compact
				/>
			</div>

			<div
				className={`flex gap-4 pt-4 ${
					onBack ? 'justify-between' : 'justify-end'
				}`}
			>
				{onBack && (
					<Button
						onClick={onBack}
						text="Voltar"
						variant="outline"
						startIcon={<CircleChevronLeft className="w-4" />}
					/>
				)}
				<button
					type="submit"
					disabled={isSubmitting || !isDirty}
					className="flex items-center gap-2 rounded-2xl bg-zinc-900 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-zinc-900/10 transition-all hover:bg-zinc-800 active:scale-95 disabled:opacity-70"
				>
					{isSubmitting ? (
						<Loader2 className="size-[18px] animate-spin" />
					) : (
						<Save size={18} />
					)}
					{isSubmitting
						? 'Salvando...'
						: submitButtonText || 'Salvar'}
				</button>
			</div>
		</form>
	);
};

export { ProfessionalForm };
